import React, { useState, useEffect } from "react";
import { Tabs, Button } from "antd";
import { FolderOutlined, TeamOutlined } from "@ant-design/icons";
import FileBrowserWrapper from "./FileBrowser.style";
import LibraryTab from "./LibraryTab";
import UploadTab from "./UploadTab";
import IntlMessages from "@iso/components/utility/intlMessages";

const { TabPane } = Tabs;

// Same files, same order? Compared by id so a caller passing an equal-but-new
// array does not restart the seeding on every render.
const sameFileSet = (a, b) =>
  Array.isArray(a)
  && Array.isArray(b)
  && a.length === b.length
  && a.every((f, i) => (f && f.id) === (b[i] && b[i].id));

const FileBrowser = ({
  visible = false,
  onCancel = () => {},
  onSelect = () => {},
  multiple = false,
  accept = null,
  maxCount = null,
  title = null,
  defaultTab = "library",
  uploadProps = {},
  initialSelection = [],
  corporateGroupId = null,
  propertyId = null,
}) => {
  const [activeScope, setActiveScope] = useState("property");
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [propertyRefreshKey, setPropertyRefreshKey] = useState(0);
  const [corporateRefreshKey, setCorporateRefreshKey] = useState(0);
  const [propertyFolderId, setPropertyFolderId] = useState(null);
  const [corporateFolderId, setCorporateFolderId] = useState(null);

  // Seed selection from initialSelection when modal opens.
  //
  // Every setter below is guarded to return the PREVIOUS value when nothing
  // actually changes, and that is load-bearing, not tidiness: `initialSelection`
  // defaults to a literal `[]`, so a caller that omits it hands this effect a
  // brand-new array on every render, and the dependency list sees a change every
  // time. Unguarded, the closed branch then set a fresh `[]` into state, which
  // re-rendered, which produced another new default, which ran the effect
  // again — an infinite loop that locked the tab and climbed in memory until
  // the browser gave up. Callers that pass a stable array never saw it, which
  // is why it sat here until a new caller omitted the prop.
  useEffect(() => {
    if (visible) {
      if (initialSelection.length > 0) {
        setSelectedFiles((prev) => (sameFileSet(prev, initialSelection) ? prev : initialSelection));
      }
    } else {
      setSelectedFiles((prev) => (prev.length ? [] : prev));
      setActiveScope((prev) => (prev === "property" ? prev : "property"));
      setActiveTab((prev) => (prev === defaultTab ? prev : defaultTab));
      setPropertyFolderId((prev) => (prev === null ? prev : null));
      setCorporateFolderId((prev) => (prev === null ? prev : null));
    }
  }, [visible, defaultTab, initialSelection]);

  const handleSelectionChange = (files) => {
    setSelectedFiles(files);
  };

  // Auto-select files that were just uploaded so the user can hit "Select"
  // immediately — previously upload only refreshed the Library list, leaving the
  // new file unselected and the Select button disabled.
  const autoSelectUploaded = (uploadedFiles) => {
    if (!Array.isArray(uploadedFiles) || uploadedFiles.length === 0) return;
    setSelectedFiles((prev) => {
      const existingIds = new Set(prev.map((f) => f.id));
      const fresh = uploadedFiles.filter((f) => f && f.id != null && !existingIds.has(f.id));
      if (fresh.length === 0) return prev;
      if (!multiple) return [fresh[fresh.length - 1]];
      const next = [...prev, ...fresh];
      return maxCount ? next.slice(0, maxCount) : next;
    });
  };

  const handlePropertyUploadSuccess = (uploadedFiles) => {
    autoSelectUploaded(uploadedFiles);
    setPropertyRefreshKey((prev) => prev + 1);
  };

  const handleCorporateUploadSuccess = (uploadedFiles) => {
    autoSelectUploaded(uploadedFiles);
    setCorporateRefreshKey((prev) => prev + 1);
  };

  const handleConfirm = () => {
    onSelect(selectedFiles);
    setSelectedFiles([]);
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    onCancel();
  };

  const modalTitle = title || <IntlMessages id="fileBrowser.title" />;

  const footer = (
    <div className="footer-bar">
      <span className="selected-count">
        {selectedFiles.length > 0
          ? `${selectedFiles.length} file(s) selected`
          : ""}
      </span>
      <div className="footer-actions">
        <Button onClick={handleCancel}>
          <IntlMessages id="fileBrowser.cancel" />
        </Button>
        <Button
          type="primary"
          disabled={selectedFiles.length === 0}
          onClick={handleConfirm}
        >
          <IntlMessages id="fileBrowser.select" />
        </Button>
      </div>
    </div>
  );

  const renderLibraryAndUpload = (scopeCorporateGroupId, scopePropertyId, refreshKey, onUploadSuccess, folderId, onFolderChange) => (
    <Tabs activeKey={activeTab} onChange={setActiveTab}>
      <TabPane
        tab={<IntlMessages id="fileBrowser.tab.library" />}
        key="library"
      >
        <LibraryTab
          visible={visible}
          accept={accept}
          multiple={multiple}
          maxCount={maxCount}
          selectedFiles={selectedFiles}
          onSelectionChange={handleSelectionChange}
          refreshKey={refreshKey}
          corporateGroupId={scopeCorporateGroupId}
          propertyId={scopePropertyId}
          onFolderChange={onFolderChange}
        />
      </TabPane>
      <TabPane
        tab={<IntlMessages id="fileBrowser.tab.upload" />}
        key="upload"
      >
        <UploadTab
          uploadProps={uploadProps}
          onUploadSuccess={onUploadSuccess}
          currentFolderId={folderId}
          corporateGroupId={scopeCorporateGroupId}
        />
      </TabPane>
    </Tabs>
  );

  return (
    <FileBrowserWrapper
      visible={visible}
      title={modalTitle}
      onCancel={handleCancel}
      footer={footer}
      width={900}
      zIndex={1100}
      forceRender
      maskClosable={false}
      destroyOnClose={false}
    >
      {corporateGroupId ? (
        <Tabs
          activeKey={activeScope}
          onChange={(key) => {
            setActiveScope(key);
            setActiveTab("library");
          }}
          type="card"
          className="scope-tabs"
        >
          <TabPane
            tab={<span><FolderOutlined /> My Files</span>}
            key="property"
          >
            {renderLibraryAndUpload(null, propertyId, propertyRefreshKey, handlePropertyUploadSuccess, propertyFolderId, setPropertyFolderId)}
          </TabPane>
          <TabPane
            tab={<span><TeamOutlined /> Corporate Library</span>}
            key="corporate"
          >
            {renderLibraryAndUpload(corporateGroupId, null, corporateRefreshKey, handleCorporateUploadSuccess, corporateFolderId, setCorporateFolderId)}
          </TabPane>
        </Tabs>
      ) : (
        renderLibraryAndUpload(null, propertyId, propertyRefreshKey, handlePropertyUploadSuccess, propertyFolderId, setPropertyFolderId)
      )}
    </FileBrowserWrapper>
  );
};

export default FileBrowser;
